import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceItemsApi, evidenceStorageLocationsApi } from '../evidenceApi';
import {
  EVIDENCE_EXTENSIONS,
  MAX_ATTACHMENTS,
  type EvidenceItemDetail,
  type EvidenceItemListItem,
  type EvidenceStorageLocationOption,
} from '../evidenceTypes';
import { useStaffOptions } from '../../../human-resources/personnel/useStaff';
import { samplePdf, sampleStoreIn } from '../utils/sampleEvidence';
import { EvidenceFilesField } from './EvidenceFilesField';

const REASONS = ['Returned from laboratory', 'Returned from court', 'Returned by another agency'];

/**
 * Stores BACK an item that was transferred out — the "stored in" movement on an item that
 * already has a chain. Like the first intake: no approval, no password, at least one document.
 * A disposed item never reaches this form; the profile does not offer it.
 */
export function EvidenceStoreInModal({
  item,
  onClose,
  onSaved,
}: {
  item: EvidenceItemListItem;
  onClose: () => void;
  onSaved: (saved: EvidenceItemDetail) => void;
}) {
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);

  const [custodianId, setCustodianId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [reason, setReason] = useState('');
  const [justification, setJustification] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [locations, setLocations] = useState<EvidenceStorageLocationOption[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    evidenceStorageLocationsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setLocations(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLocations(false);
      });

    return () => controller.abort();
  }, []);

  const valid =
    custodianId !== '' &&
    locationId !== '' &&
    reason.trim() !== '' &&
    justification.trim() !== '' &&
    attachments.length > 0;

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved = await evidenceItemsApi.storeIn(
        item.id,
        {
          custodianPersonnelId: custodianId,
          storageLocationId: locationId,
          reason: reason.trim(),
          justification: justification.trim(),
        },
        attachments,
      );

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not store the evidence in.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'evidence-store-in' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Store in ${item.inventoryNumber}`}
      size="lg"
      id="evidence-store-in-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleStoreIn({ staffIds: staff.map((s) => s.value), locationIds: locations.map((l) => l.id) });
          setCustodianId(sample.custodianPersonnelId);
          setLocationId(sample.storageLocationId);
          setReason(sample.reason);
          setJustification(sample.justification);
          setAttachments([samplePdf('sample-return-receipt.pdf')]);
        },
        disabled: saving || loadingLocations,
      }}
      confirm={{ id: 'evidence-store-in-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="evidence-store-in-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="evidence-store-in-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="evidence-store-in-custodian-personnel-id"
            label="Custodian"
            required
            description="The custodian receiving it back."
            placeholder="Choose an employee"
            value={custodianId}
            onChange={setCustodianId}
            options={staff}
            remote
            loading={loadingStaff}
            disabled={saving}
            error={error?.fieldError('custodianPersonnelId')}
          />

          <Select
            id="evidence-store-in-storage-location-id"
            label="Storage location"
            required
            description="Where it is kept in the evidence room from now on — a digital item's media included."
            placeholder="Choose a location"
            value={locationId}
            onChange={setLocationId}
            options={locations.map((l) => ({ value: l.id, label: l.name, sublabel: l.code, keywords: l.code }))}
            remote
            loading={loadingLocations}
            disabled={saving}
            error={error?.fieldError('storageLocationId')}
          />

          <Select
            id="evidence-store-in-reason"
            label="Reason"
            required
            placeholder="Choose or type a reason"
            value={reason}
            onChange={setReason}
            allowCustom
            options={REASONS.map((r) => ({ value: r, label: r }))}
            disabled={saving}
            error={error?.fieldError('reason')}
          />
        </div>

        <Textarea
          id="evidence-store-in-justification"
          label="Justification"
          required
          description="Written as it will stand on the chain of custody."
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          maxLength={2000}
          disabled={saving}
          error={error?.fieldError('justification')}
        />

        <EvidenceFilesField
          id="evidence-store-in-attachments"
          label="Documents"
          required
          multiple
          max={MAX_ATTACHMENTS}
          description="At least one: the return receipt, a photograph, or the signed form."
          accept={EVIDENCE_EXTENSIONS}
          files={attachments}
          onChange={setAttachments}
          disabled={saving}
          error={error?.fieldError('attachments')}
        />
      </div>
    </Modal>
  );
}
